import React, { useRef, useState, useEffect } from 'react';
import { submitLead } from '../api/intake';
import { track, analyticsSessionId } from '../analytics/track';

const EVENT_TYPES = ['Wedding', 'Corporate Event', 'Graduation', 'Birthday', 'Other'];

const PACKAGES = [
  'Retro Booth — Unlimited Digital Photos',
  'Retro Booth — Unlimited Prints + Digital Photos',
  'Marquee Letters',
  'Retro Booth + Marquee Letters',
  'Not sure yet',
];

const EMPTY = {
  name: '',
  email: '',
  phone: '',
  eventDate: '',
  eventType: '',
  packageName: '',
  notes: '',
  website: '', // spam trap — humans never see or fill this
};

const MAX_LEN = 2000; // the endpoint truncates beyond this; cap it here so nothing is lost silently
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Local calendar date as YYYY-MM-DD (toISOString would give UTC and roll over in US evenings).
function todayLocal() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Whole days from today to the event — lead time is useful for planning, and isn't personal data.
function daysUntil(isoDate) {
  const [y, m, d] = isoDate.split('-').map(Number);
  const [ty, tm, td] = todayLocal().split('-').map(Number);
  return Math.round((Date.UTC(y, m - 1, d) - Date.UTC(ty, tm - 1, td)) / 86400000);
}

function validate(values) {
  const errors = {};
  if (!values.name.trim()) errors.name = 'Please enter your name.';
  if (!values.email.trim()) errors.email = 'Please enter your email.';
  else if (!EMAIL_RE.test(values.email.trim())) errors.email = 'Please enter a valid email address.';
  const phoneDigits = values.phone.replace(/\D/g, '');
  if (!phoneDigits) errors.phone = 'Please enter your phone number.';
  else if (phoneDigits.length < 10 || phoneDigits.length > 15) errors.phone = 'Please enter a valid phone number.';
  if (!values.eventDate) errors.eventDate = 'Please choose your event date.';
  else if (values.eventDate < todayLocal()) errors.eventDate = 'Please choose a date in the future.';
  return errors;
}

const FIELD_ORDER = ['name', 'email', 'phone', 'eventDate'];

const LeadForm = () => {
  const [values, setValues] = useState(EMPTY);
  const [fieldErrors, setFieldErrors] = useState({});
  const [serverErrors, setServerErrors] = useState([]);
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [submittedEmail, setSubmittedEmail] = useState('');
  const fieldRefs = useRef({});
  const successRef = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (status === 'success' && successRef.current) successRef.current.focus();
  }, [status]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (!startedRef.current && name !== 'website') {
      startedRef.current = true;
      track('lead_form_start');
    }
    setValues((v) => ({ ...v, [name]: value }));
    if (fieldErrors[name]) setFieldErrors((errs) => ({ ...errs, [name]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'submitting') return;

    const errors = validate(values);
    setFieldErrors(errors);
    setServerErrors([]);
    const firstInvalid = FIELD_ORDER.find((f) => errors[f]);
    if (firstInvalid) {
      track('lead_form_invalid', { fields: Object.keys(errors).join(',') });
      fieldRefs.current[firstInvalid]?.focus();
      return;
    }

    setStatus('submitting');
    try {
      const payload = Object.fromEntries(
        Object.entries(values).map(([k, v]) => [k, v.trim()])
      );
      // Anonymous analytics session, so a lead can later be linked to the pages that led to it.
      // The intake endpoint ignores unknown fields until the backend stores it.
      payload.analyticsSessionId = analyticsSessionId;
      const result = await submitLead(payload);
      if (result.ok) {
        // categories and lead time only — never the name, email, phone or notes
        track('lead_form_success', {
          eventType: payload.eventType || null,
          packageName: payload.packageName || null,
          daysUntilEvent: daysUntil(payload.eventDate),
        });
        setSubmittedEmail(payload.email);
        setStatus('success');
      } else {
        track('lead_form_rejected', { count: result.errors.length });
        setServerErrors(result.errors);
        setStatus('idle');
      }
    } catch {
      track('lead_form_error');
      setStatus('error'); // keep what they typed so they can retry
    }
  };

  if (status === 'success') {
    const firstName = values.name.trim().split(/\s+/)[0];
    return (
      <div className="lead-form-success" role="status" tabIndex={-1} ref={successRef}>
        <h2>Thanks, {firstName}!</h2>
        <p>
          We&rsquo;ve received your details. We&rsquo;ll check availability for your date and get back to you
          at <strong>{submittedEmail}</strong> shortly.
        </p>
      </div>
    );
  }

  const field = (name) => ({
    id: `lead-${name}`,
    name,
    value: values[name],
    onChange: handleChange,
    maxLength: MAX_LEN,
    'aria-invalid': fieldErrors[name] ? 'true' : undefined,
    'aria-describedby': fieldErrors[name] ? `lead-${name}-error` : undefined,
    ref: (el) => { fieldRefs.current[name] = el; },
  });

  const error = (name) =>
    fieldErrors[name] ? (
      <p className="lead-form-error" id={`lead-${name}-error`}>{fieldErrors[name]}</p>
    ) : null;

  return (
    <form className="lead-form" onSubmit={handleSubmit} noValidate>
      {serverErrors.length > 0 && (
        <div className="lead-form-alert" role="alert">
          <p>Please check the following:</p>
          <ul>{serverErrors.map((msg) => <li key={msg}>{msg}</li>)}</ul>
        </div>
      )}

      {status === 'error' && (
        <div className="lead-form-alert" role="alert">
          <p>
            Sorry &mdash; we couldn&rsquo;t send your details. Please try again, or email us at{' '}
            <a href="mailto:contact@discoduckevents.com">contact@discoduckevents.com</a>.
          </p>
        </div>
      )}

      <div className="lead-form-grid">
        <div className="lead-form-field">
          <label htmlFor="lead-name">Full name <span aria-hidden="true">*</span></label>
          <input type="text" autoComplete="name" required {...field('name')} />
          {error('name')}
        </div>

        <div className="lead-form-field">
          <label htmlFor="lead-email">Email <span aria-hidden="true">*</span></label>
          <input type="email" autoComplete="email" inputMode="email" required {...field('email')} />
          {error('email')}
        </div>

        <div className="lead-form-field">
          <label htmlFor="lead-phone">Phone <span aria-hidden="true">*</span></label>
          <input type="tel" autoComplete="tel" inputMode="tel" required {...field('phone')} />
          {error('phone')}
        </div>

        <div className="lead-form-field">
          <label htmlFor="lead-eventDate">Event date <span aria-hidden="true">*</span></label>
          <input type="date" min={todayLocal()} required {...field('eventDate')} />
          {error('eventDate')}
        </div>

        <div className="lead-form-field">
          <label htmlFor="lead-eventType">Event type</label>
          <select {...field('eventType')}>
            <option value="">Select…</option>
            {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        <div className="lead-form-field">
          <label htmlFor="lead-packageName">Package</label>
          <select {...field('packageName')}>
            <option value="">Select…</option>
            {PACKAGES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>

        <div className="lead-form-field lead-form-field--full">
          <label htmlFor="lead-notes">Anything else we should know?</label>
          <textarea rows={4} placeholder="Venue, timings, guest count, indoor or outdoor…" {...field('notes')} />
        </div>
      </div>

      {/* Spam trap: off-screen and skipped by keyboard and screen readers. Bots fill it; people don't. */}
      <div className="lead-form-trap" aria-hidden="true">
        <label htmlFor="lead-website">Website</label>
        <input type="text" id="lead-website" name="website" value={values.website}
          onChange={handleChange} tabIndex={-1} autoComplete="off" />
      </div>

      <p className="lead-form-required-note">
        <span aria-hidden="true">*</span> Required &middot; Packages from $450 &middot; No commitment
      </p>

      <button type="submit" className="hero-cta hero-cta-primary lead-form-submit" disabled={status === 'submitting'}>
        {status === 'submitting' ? 'Sending…' : 'Check Availability'}
      </button>
    </form>
  );
};

export default LeadForm;
